import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertCircle, AlertTriangle, ArrowLeft, CheckCheck, ChevronLeft, ChevronRight,
  Download, ExternalLink, Info, Play, RefreshCw, Shield, ShieldCheck,
  SquareCheck, Trash2, Wrench, X, Search, Filter,
} from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import api from '../services/api';

// ─── Constants ────────────────────────────────────────────────────────────────

const PAGE_SIZE = 25;

const SEVERITY_STYLE = {
  HIGH:   'bg-red-50 text-red-700 border-red-200',
  MEDIUM: 'bg-amber-50 text-amber-700 border-amber-200',
  LOW:    'bg-blue-50 text-blue-700 border-blue-200',
  INFO:   'bg-slate-100 text-slate-500 border-slate-200',
};

const SOURCE_STYLE = {
  RULE: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  AI:   'bg-purple-50 text-purple-700 border-purple-200',
};

function SevBadge({ severity }) {
  return (
    <Badge variant="outline" className={`text-xs font-medium ${SEVERITY_STYLE[severity] ?? SEVERITY_STYLE.INFO}`}
      data-testid={`sev-badge-${severity}`}>
      {severity}
    </Badge>
  );
}

function SrcBadge({ source }) {
  return (
    <Badge variant="outline" className={`text-xs font-medium ${SOURCE_STYLE[source] ?? SOURCE_STYLE.RULE}`}>
      {source}
    </Badge>
  );
}

// ─── Stat Card ────────────────────────────────────────────────────────────────

function StatCard({ label, value, sub, colorClass = 'text-slate-700' }) {
  return (
    <Card className="flex-1 min-w-0">
      <CardContent className="pt-4 pb-3 px-4">
        <p className="text-xs text-slate-500 font-medium uppercase tracking-wide truncate">{label}</p>
        <p className={`text-2xl font-bold mt-0.5 ${colorClass}`} data-testid={`stat-${label.toLowerCase().replace(/\s+/g,'-')}`}>
          {value ?? '—'}
        </p>
        {sub && <p className="text-xs text-slate-400 mt-0.5 truncate">{sub}</p>}
      </CardContent>
    </Card>
  );
}

// ─── Findings Table ───────────────────────────────────────────────────────────

function FindingsTable({ findings, selected, onToggle, onToggleAll }) {
  const [page, setPage] = useState(1);
  const [severityFilter, setSeverityFilter] = useState('ALL');
  const [ruleFilter, setRuleFilter] = useState('ALL');
  const [search, setSearch] = useState('');
  const [canApplyOnly, setCanApplyOnly] = useState(false);

  // Reset page when findings change
  useEffect(() => { setPage(1); }, [findings]);

  const rules = useMemo(() => {
    const s = new Set(findings.map(f => f.rule_id));
    return ['ALL', ...Array.from(s).sort()];
  }, [findings]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return findings.filter(f => {
      if (severityFilter !== 'ALL' && f.severity !== severityFilter) return false;
      if (ruleFilter !== 'ALL' && f.rule_id !== ruleFilter) return false;
      if (canApplyOnly && !f.can_auto_apply) return false;
      if (term) {
        const haystack = `${f.product_name} ${f.product_code ?? ''} ${f.issue ?? ''}`.toLowerCase();
        if (!haystack.includes(term)) return false;
      }
      return true;
    });
  }, [findings, severityFilter, ruleFilter, search, canApplyOnly]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const allPageSelected = pageItems.length > 0 && pageItems.every(f => selected.has(f.finding_id));
  const somePageSelected = pageItems.some(f => selected.has(f.finding_id));

  const selectedCount = filtered.filter(f => selected.has(f.finding_id)).length;
  const applyEligible = filtered.filter(f => f.can_auto_apply);

  return (
    <div data-testid="findings-table-wrapper">
      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center mb-3">
        <div className="relative flex-1 min-w-40 max-w-xs">
          <Search className="absolute left-2 top-2 h-4 w-4 text-slate-400 pointer-events-none" />
          <Input
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search product or issue…"
            className="pl-8 h-8 text-sm"
            data-testid="findings-search"
          />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-2 top-2 text-slate-400 hover:text-slate-600">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        <Select value={severityFilter} onValueChange={v => { setSeverityFilter(v); setPage(1); }}>
          <SelectTrigger className="h-8 text-sm w-36" data-testid="severity-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All severities</SelectItem>
            <SelectItem value="HIGH">High</SelectItem>
            <SelectItem value="MEDIUM">Medium</SelectItem>
            <SelectItem value="LOW">Low</SelectItem>
            <SelectItem value="INFO">Info</SelectItem>
          </SelectContent>
        </Select>

        <Select value={ruleFilter} onValueChange={v => { setRuleFilter(v); setPage(1); }}>
          <SelectTrigger className="h-8 text-sm w-52 font-mono" data-testid="rule-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {rules.map(r => (
              <SelectItem key={r} value={r} className="font-mono text-xs">{r}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <label className="flex items-center gap-1.5 text-sm text-slate-600 cursor-pointer select-none">
          <Switch
            checked={canApplyOnly}
            onCheckedChange={v => { setCanApplyOnly(v); setPage(1); }}
            data-testid="can-apply-filter"
          />
          Auto-apply only
        </label>

        <span className="text-xs text-slate-400 ml-auto">
          {filtered.length} finding{filtered.length !== 1 ? 's' : ''}
          {selectedCount > 0 && <> · <strong>{selectedCount} selected</strong></>}
        </span>
      </div>

      {/* Bulk selection helpers */}
      <div className="flex gap-2 mb-2">
        <Button
          variant="outline" size="sm"
          onClick={() => onToggleAll(pageItems)}
          data-testid="select-page-btn"
          className="text-xs h-7"
        >
          <SquareCheck className="h-3.5 w-3.5 mr-1" />
          {allPageSelected ? 'Deselect page' : 'Select page'}
        </Button>
        <Button
          variant="outline" size="sm"
          onClick={() => onToggleAll(applyEligible)}
          data-testid="select-auto-apply-btn"
          className="text-xs h-7"
        >
          <CheckCheck className="h-3.5 w-3.5 mr-1" />
          Select all auto-apply ({applyEligible.length})
        </Button>
        {selectedCount > 0 && (
          <Button
            variant="ghost" size="sm"
            onClick={() => onToggleAll([])}
            data-testid="clear-selection-btn"
            className="text-xs h-7 text-slate-500"
          >
            <X className="h-3.5 w-3.5 mr-1" />
            Clear selection
          </Button>
        )}
      </div>

      {/* Table */}
      <div className="border rounded-md overflow-x-auto">
        <table className="w-full text-sm" data-testid="findings-table">
          <thead>
            <tr className="border-b bg-slate-50 text-xs text-slate-500 font-medium uppercase tracking-wide">
              <th className="w-8 px-3 py-2">
                <input
                  type="checkbox"
                  checked={allPageSelected && pageItems.length > 0}
                  ref={el => { if (el) el.indeterminate = somePageSelected && !allPageSelected; }}
                  onChange={() => onToggleAll(pageItems)}
                  className="rounded"
                  data-testid="select-all-page-checkbox"
                />
              </th>
              <th className="px-3 py-2 text-left">Severity</th>
              <th className="px-3 py-2 text-left">Product</th>
              <th className="px-3 py-2 text-left">Category</th>
              <th className="px-3 py-2 text-left">Issue</th>
              <th className="px-3 py-2 text-left">Proposed Change</th>
              <th className="px-3 py-2 text-left">Evidence</th>
              <th className="px-3 py-2 text-left">Conf</th>
              <th className="px-3 py-2 text-left">Src</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {pageItems.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-3 py-8 text-center text-slate-400 text-sm">
                  No findings match current filters.
                </td>
              </tr>
            ) : pageItems.map(f => (
              <tr
                key={f.finding_id}
                className={`hover:bg-slate-50 transition-colors ${selected.has(f.finding_id) ? 'bg-sky-50' : ''}`}
                data-testid={`finding-row-${f.finding_id}`}
              >
                <td className="px-3 py-2">
                  <input
                    type="checkbox"
                    checked={selected.has(f.finding_id)}
                    onChange={() => onToggle(f.finding_id)}
                    disabled={!f.can_auto_apply}
                    title={!f.can_auto_apply ? 'Exception-only — manual review required' : undefined}
                    className="rounded disabled:opacity-40"
                    data-testid={`finding-checkbox-${f.finding_id}`}
                  />
                </td>
                <td className="px-3 py-2">
                  <SevBadge severity={f.severity} />
                </td>
                <td className="px-3 py-2 max-w-[200px]">
                  <div className="font-medium text-slate-800 truncate" title={f.product_name}>
                    {f.product_name}
                  </div>
                  {f.product_code && (
                    <div className="text-xs text-slate-400 font-mono">{f.product_code}</div>
                  )}
                  {f.mercure_reference && (
                    <div className="text-xs text-emerald-600 truncate" title={`Mercure ref: ${f.mercure_reference}`}>
                      ref: {f.mercure_reference}
                    </div>
                  )}
                </td>
                <td className="px-3 py-2 text-xs text-slate-500 whitespace-nowrap">
                  {f.category && <div>{f.category}</div>}
                  {f.subcategory && <div className="text-slate-400">{f.subcategory}</div>}
                </td>
                <td className="px-3 py-2 max-w-[220px]">
                  <div className="text-xs text-slate-700 leading-snug">{f.issue}</div>
                  <div className="font-mono text-xs text-slate-400 mt-0.5">{f.rule_id}</div>
                </td>
                <td className="px-3 py-2 max-w-[200px]">
                  {f.proposed_change ? (
                    <div className="text-xs text-sky-700 leading-snug">{f.proposed_change}</div>
                  ) : (
                    <span className="text-xs text-slate-300 italic">manual review</span>
                  )}
                </td>
                <td className="px-3 py-2 max-w-[200px]">
                  <div className="text-xs text-slate-500 leading-snug line-clamp-2" title={f.evidence_detail}>
                    {f.evidence_detail}
                  </div>
                </td>
                <td className="px-3 py-2 text-xs font-mono whitespace-nowrap">
                  {f.confidence > 0 ? (
                    <span className={f.confidence >= 0.95 ? 'text-emerald-600 font-semibold' : f.confidence >= 0.85 ? 'text-amber-600' : 'text-slate-400'}>
                      {Math.round(f.confidence * 100)}%
                    </span>
                  ) : (
                    <span className="text-slate-300">—</span>
                  )}
                </td>
                <td className="px-3 py-2">
                  <SrcBadge source={f.source} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between mt-3 text-sm text-slate-600">
        <span className="text-xs text-slate-400">
          Page {currentPage} of {totalPages} · {filtered.length} findings
        </span>
        <div className="flex items-center gap-1">
          <Button variant="outline" size="sm" className="h-7 w-7 p-0"
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            data-testid="prev-page-btn">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            const startPage = Math.max(1, Math.min(currentPage - 2, totalPages - 4));
            const p = startPage + i;
            if (p > totalPages) return null;
            return (
              <Button
                key={p}
                variant={p === currentPage ? 'default' : 'outline'}
                size="sm"
                className="h-7 w-7 p-0 text-xs"
                onClick={() => setPage(p)}
                data-testid={`page-btn-${p}`}
              >
                {p}
              </Button>
            );
          })}
          <Button variant="outline" size="sm" className="h-7 w-7 p-0"
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            data-testid="next-page-btn">
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Run History Row ──────────────────────────────────────────────────────────

function RunHistoryRow({ run, onExport }) {
  const statusStyle = run.status === 'applied'
    ? 'text-emerald-700 bg-emerald-50 border-emerald-200'
    : 'text-slate-600 bg-slate-50 border-slate-200';
  return (
    <tr className="border-b last:border-0 hover:bg-slate-50 text-sm" data-testid={`run-row-${run.run_id}`}>
      <td className="px-3 py-2 font-mono text-xs text-slate-400 whitespace-nowrap">
        {run.run_id.slice(0, 8)}…
      </td>
      <td className="px-3 py-2 text-xs text-slate-500 whitespace-nowrap">
        {run.scanned_at ? new Date(run.scanned_at).toLocaleString() : '—'}
      </td>
      <td className="px-3 py-2 text-xs">
        {run.reference_site_id
          ? <span className="font-medium text-sky-700">Site {run.reference_site_id}</span>
          : <span className="text-slate-400">Full scan</span>}
      </td>
      <td className="px-3 py-2">
        <Badge variant="outline" className={`text-xs ${statusStyle}`}>{run.status}</Badge>
      </td>
      <td className="px-3 py-2 text-xs text-slate-500 text-right">{run.total_findings ?? 0}</td>
      <td className="px-3 py-2 text-xs text-slate-500 text-right">{run.auto_apply_eligible ?? 0}</td>
      <td className="px-3 py-2 text-xs text-slate-500 text-right">{run.applied_count ?? 0}</td>
      <td className="px-3 py-2">
        {run.status === 'applied' && (
          <div className="flex gap-1">
            <Button variant="outline" size="sm" className="h-6 text-xs px-2"
              onClick={() => onExport(run.run_id, 'csv')}
              data-testid={`export-csv-${run.run_id}`}>
              <Download className="h-3 w-3 mr-1" />CSV
            </Button>
            <Button variant="outline" size="sm" className="h-6 text-xs px-2"
              onClick={() => onExport(run.run_id, 'json')}
              data-testid={`export-json-${run.run_id}`}>
              <Download className="h-3 w-3 mr-1" />JSON
            </Button>
          </div>
        )}
      </td>
    </tr>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function ProductMasterAuditPage() {
  const navigate = useNavigate();

  // Environment
  const [envInfo, setEnvInfo] = useState(null);

  // Sites for dropdown
  const [sites, setSites] = useState([]);
  const [sitesLoading, setSitesLoading] = useState(true);

  // Scan config
  const [referenceSiteId, setReferenceSiteId] = useState(null);
  const [scanning, setScanning] = useState(false);

  // Current scan result
  const [scanResult, setScanResult] = useState(null);  // ScanSummary
  const [selected, setSelected] = useState(new Set());  // finding_id set

  // Apply config
  const [dryRun, setDryRun] = useState(true);
  const [applying, setApplying] = useState(false);
  const [applyResult, setApplyResult] = useState(null);

  // Task 1 heal workflow state
  const [task1DryRunDone, setTask1DryRunDone] = useState(false);
  const [task1DryRunResult, setTask1DryRunResult] = useState(null);
  const [task1Applying, setTask1Applying] = useState(false);
  const [task1ApplyResult, setTask1ApplyResult] = useState(null);
  const [task1ApplyConfirm, setTask1ApplyConfirm] = useState(false);
  const [task1RunningDryRun, setTask1RunningDryRun] = useState(false);
  const [task1Rescanning, setTask1Rescanning] = useState(false);
  const [task1RescanResult, setTask1RescanResult] = useState(null);

  // Run history
  const [runs, setRuns] = useState([]);
  const [runsLoading, setRunsLoading] = useState(false);

  // ── Load sites + environment ─────────────────────────────────────────────────
  useEffect(() => {
    const loadSites = async () => {
      setSitesLoading(true);
      try {
        const r = await api.get('/sites');
        const list = Array.isArray(r.data) ? r.data : (r.data?.sites ?? r.data?.results ?? []);
        setSites(list.filter(s => s.is_active !== false));
      } catch {
        toast.error('Failed to load sites');
      } finally {
        setSitesLoading(false);
      }
    };
    loadSites();
    // Load environment info (non-critical)
    api.get('/admin/product-master/environment')
      .then(r => setEnvInfo(r.data))
      .catch(() => {/* non-critical */});
  }, []);

  // ── Load run history ────────────────────────────────────────────────────────
  const loadRuns = useCallback(async () => {
    setRunsLoading(true);
    try {
      const r = await api.get('/admin/product-master/runs?limit=20');
      setRuns(r.data ?? []);
    } catch {
      // Silently fail if table doesn't exist yet
      setRuns([]);
    } finally {
      setRunsLoading(false);
    }
  }, []);

  useEffect(() => { loadRuns(); }, [loadRuns]);

  // ── Run scan ─────────────────────────────────────────────────────────────────
  const handleScan = async () => {
    setScanning(true);
    setScanResult(null);
    setSelected(new Set());
    setApplyResult(null);
    try {
      const params = referenceSiteId ? `?reference_site_id=${referenceSiteId}` : '';
      const r = await api.get(`/admin/product-master/scan${params}`);
      setScanResult(r.data);
      toast.success(
        `Scan complete: ${r.data.findings?.length} finding${r.data.findings?.length !== 1 ? 's' : ''} across ${r.data.total_products_scanned} products`
      );
      await loadRuns();
    } catch (err) {
      toast.error(err.response?.data?.detail ?? 'Scan failed');
    } finally {
      setScanning(false);
    }
  };

  // ── Selection helpers ─────────────────────────────────────────────────────
  const handleToggle = (findingId) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(findingId)) next.delete(findingId);
      else next.add(findingId);
      return next;
    });
  };

  const handleToggleAll = (items) => {
    if (items.length === 0) {
      setSelected(new Set());
      return;
    }
    const allSelected = items.every(f => selected.has(f.finding_id));
    setSelected(prev => {
      const next = new Set(prev);
      if (allSelected) items.forEach(f => next.delete(f.finding_id));
      else items.forEach(f => { if (f.can_auto_apply) next.add(f.finding_id); });
      return next;
    });
  };

  // ── Apply ───────────────────────────────────────────────────────────────────
  const handleApplySelected = async () => {
    if (!scanResult) return;
    if (selected.size === 0) { toast.error('Select at least one finding'); return; }
    setApplying(true);
    setApplyResult(null);
    try {
      const r = await api.post('/admin/product-master/apply', {
        run_id: scanResult.run_id,
        finding_ids: Array.from(selected),
        dry_run: dryRun,
      });
      setApplyResult(r.data);
      toast.success(
        dryRun
          ? `Dry-run complete: ${r.data.applied} would be applied`
          : `Applied ${r.data.applied} change${r.data.applied !== 1 ? 's' : ''} (${r.data.skipped} skipped)`
      );
      if (!dryRun) {
        setSelected(new Set());
        await loadRuns();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail ?? 'Apply failed');
    } finally {
      setApplying(false);
    }
  };

  const handleApplyTask1 = async () => {
    if (!scanResult) return;
    if (!referenceSiteId) {
      toast.error('Task 1 apply requires a reference site. Select a site and re-scan.');
      return;
    }
    if (scanResult.task1_eligible === 0) {
      toast.error('No Task 1 high-confidence findings in this scan run.');
      return;
    }
    setApplying(true);
    setApplyResult(null);
    try {
      const r = await api.post('/admin/product-master/apply-task1', {
        run_id: scanResult.run_id,
        dry_run: dryRun,
        notes: 'Task 1 high-confidence apply via Admin UI',
      });
      setApplyResult(r.data);
      toast.success(
        dryRun
          ? `Dry-run: ${r.data.applied} Task 1 changes would be applied`
          : `Task 1: applied ${r.data.applied} change${r.data.applied !== 1 ? 's' : ''}`
      );
      if (!dryRun) await loadRuns();
    } catch (err) {
      toast.error(err.response?.data?.detail ?? 'Task 1 apply failed');
    } finally {
      setApplying(false);
    }
  };

  // ── Task 1 Dedicated Workflow ─────────────────────────────────────────────
  const handleTask1DryRun = async () => {
    if (!scanResult) return;
    setTask1RunningDryRun(true);
    setTask1DryRunResult(null);
    try {
      const r = await api.post('/admin/product-master/apply-task1', {
        run_id: scanResult.run_id,
        dry_run: true,
        notes: 'Task 1 dry-run via dedicated heal workflow',
      });
      setTask1DryRunResult(r.data);
      setTask1DryRunDone(true);
      toast.success(`Task 1 dry-run: ${r.data.applied} change${r.data.applied !== 1 ? 's' : ''} would be applied`);
    } catch (err) {
      toast.error(err.response?.data?.detail ?? 'Task 1 dry-run failed');
    } finally {
      setTask1RunningDryRun(false);
    }
  };

  const handleTask1Apply = async () => {
    if (!task1DryRunDone || !scanResult) return;
    setTask1Applying(true);
    setTask1ApplyResult(null);
    setTask1ApplyConfirm(false);
    try {
      const r = await api.post('/admin/product-master/apply-task1', {
        run_id: scanResult.run_id,
        dry_run: false,
        notes: 'Task 1 explicit apply via dedicated heal workflow',
      });
      setTask1ApplyResult(r.data);
      toast.success(`Task 1 applied: ${r.data.applied} change${r.data.applied !== 1 ? 's' : ''}`);
      await loadRuns();
    } catch (err) {
      toast.error(err.response?.data?.detail ?? 'Task 1 apply failed');
    } finally {
      setTask1Applying(false);
    }
  };

  const handleTask1Rescan = async () => {
    setTask1Rescanning(true);
    setTask1RescanResult(null);
    try {
      const params = referenceSiteId ? `?reference_site_id=${referenceSiteId}` : '';
      const r = await api.get(`/admin/product-master/scan${params}`);
      setTask1RescanResult(r.data);
      if (r.data.task1_eligible === 0) {
        toast.success('Re-scan complete — zero Task 1 candidates remaining.');
      } else {
        toast.warning(`Re-scan: ${r.data.task1_eligible} Task 1 candidate(s) still remain.`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail ?? 'Re-scan failed');
    } finally {
      setTask1Rescanning(false);
    }
  };

  // ── Export ────────────────────────────────────────────────────────────────
  const handleExport = async (runId, fmt) => {
    try {
      const r = await api.get(`/admin/product-master/runs/${runId}/export?fmt=${fmt}`, {
        responseType: fmt === 'csv' ? 'blob' : 'json',
      });
      if (fmt === 'csv') {
        const url = URL.createObjectURL(r.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = `audit_run_${runId.slice(0, 8)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
      } else {
        const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `audit_run_${runId.slice(0, 8)}.json`;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch {
      toast.error('Export failed');
    }
  };

  // ── Verify site isolation ────────────────────────────────────────────────
  const handleVerifyIsolation = async () => {
    try {
      const r = await api.get('/admin/product-master/verify-site-isolation');
      const d = r.data;
      const counts = d.current_site_counts;
      const proof = d.latest_run_proof;
      const pass = proof
        ? (proof.site_counts_before && proof.site_counts_after &&
           Object.keys(proof.site_counts_before).every(t =>
             proof.site_counts_before[t] === proof.site_counts_after[t]
           ))
        : null;

      if (counts) {
        const countStr = Object.entries(counts)
          .map(([t, c]) => `${t.replace('site_', '')}: ${c}`)
          .join(' · ');
        if (pass === true) {
          toast.success(`Site isolation PASS — ${countStr}`);
        } else if (pass === false) {
          toast.error('Site isolation FAIL — site table counts changed. Investigate immediately.');
        } else {
          toast.info(`No applied runs yet. Current counts: ${countStr}`);
        }
      } else {
        toast.info('Isolation check returned empty counts');
      }
    } catch {
      toast.error('Isolation check failed');
    }
  };

  // ── Severity counts ──────────────────────────────────────────────────────
  const sevCounts = useMemo(() => {
    if (!scanResult?.findings) return { HIGH: 0, MEDIUM: 0, LOW: 0, INFO: 0 };
    return scanResult.findings.reduce((acc, f) => {
      acc[f.severity] = (acc[f.severity] ?? 0) + 1;
      return acc;
    }, { HIGH: 0, MEDIUM: 0, LOW: 0, INFO: 0 });
  }, [scanResult]);

  const selectedApplyCount = scanResult
    ? scanResult.findings.filter(f => selected.has(f.finding_id) && f.can_auto_apply).length
    : 0;

  const selectedSiteObj = sites.find(s => String(s.id) === String(referenceSiteId));

  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div className="max-w-screen-xl mx-auto p-6 space-y-6" data-testid="product-master-audit-page">

      {/* Environment Banner */}
      {envInfo && (
        <div className={`flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium
          ${envInfo.is_production
            ? 'bg-red-50 border-red-300 text-red-800'
            : envInfo.color === 'amber'
            ? 'bg-amber-50 border-amber-300 text-amber-800'
            : 'bg-blue-50 border-blue-300 text-blue-800'
          }`}
          data-testid="env-banner"
        >
          <ShieldCheck className="h-4 w-4 shrink-0" />
          <span>
            Environment: <strong>{envInfo.label}</strong>
            {envInfo.is_production && (
              <span className="ml-2 font-bold text-red-700">
                — PRODUCTION: Heal will write to the live product master.
                Ensure dry-run has been reviewed before applying.
              </span>
            )}
          </span>
        </div>
      )}

      {/* Pre-scan Getting Started — visible only before first scan runs */}
      {!scanResult && envInfo && (
        <div
          className={`rounded-lg border px-4 py-3 text-sm
            ${envInfo.is_production
              ? 'bg-red-50 border-red-200 text-red-900'
              : 'bg-slate-50 border-slate-200 text-slate-700'
            }`}
          data-testid="pre-scan-guide"
        >
          <p className="font-semibold mb-1">
            {envInfo.is_production
              ? 'Production — follow the three-step sequence below before any data is written:'
              : 'To begin, follow this three-step sequence:'}
          </p>
          <ol className="list-decimal ml-4 space-y-0.5">
            <li><strong>Run Scan</strong> — the Task 1 Heal workflow and all findings appear here after scanning</li>
            <li><strong>Run Dry-run</strong> — reviews every proposed change; nothing is written until this step completes</li>
            <li><strong>Apply</strong> — explicit confirmation dialog; writes only after dry-run has been reviewed</li>
          </ol>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate('/stocktaking/dashboard')}
          className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 transition-colors shrink-0"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <div className="flex-1" />
        <Button variant="outline" size="sm" onClick={handleVerifyIsolation} data-testid="verify-isolation-btn">
          <ShieldCheck className="w-4 h-4 mr-1.5" />
          Verify Site Isolation
        </Button>
        <Button variant="outline" size="sm" onClick={loadRuns} disabled={runsLoading} data-testid="refresh-runs-btn">
          <RefreshCw className={`w-4 h-4 mr-1.5 ${runsLoading ? 'animate-spin' : ''}`} />
          Refresh History
        </Button>
      </div>

      <header>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
          Product Master Audit &amp; Cleanup
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Evidence-based scan of the product master. Identifies invalid measure defaults,
          all-caps names, duplicate codes and postmix gaps. Writes only to{' '}
          <code className="text-xs bg-slate-100 px-1 py-0.5 rounded">product_units_of_measure</code> and{' '}
          <code className="text-xs bg-slate-100 px-1 py-0.5 rounded">beverage_products.product_name</code>.
          Site tables are never modified.
        </p>
      </header>

      {/* Scan configuration */}
      <Card data-testid="scan-config-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Play className="h-4 w-4 text-sky-600" />
            Scan Configuration
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-52 max-w-xs">
              <Label className="text-xs text-slate-600 mb-1 block">
                Reference site <span className="text-slate-400 font-normal">(Level-1 Mercure evidence)</span>
              </Label>
              <Select
                value={referenceSiteId ? String(referenceSiteId) : '__none__'}
                onValueChange={v => setReferenceSiteId(v === '__none__' ? null : v)}
                disabled={sitesLoading}
              >
                <SelectTrigger data-testid="site-picker" className="h-9">
                  <SelectValue placeholder={sitesLoading ? 'Loading sites…' : 'None — full scan'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">None — full product master scan</SelectItem>
                  {sites.map(s => (
                    <SelectItem key={s.id} value={String(s.id)} data-testid={`site-opt-${s.id}`}>
                      {s.name} <span className="text-slate-400 text-xs ml-1">(ID {s.id})</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button
              onClick={handleScan}
              disabled={scanning}
              className="h-9"
              data-testid="run-scan-btn"
            >
              <Play className={`h-4 w-4 mr-1.5 ${scanning ? 'animate-pulse' : ''}`} />
              {scanning ? 'Scanning…' : 'Run Scan'}
            </Button>

            {!scanning && scanResult && (
              <span className="text-xs text-slate-400 self-center">
                Run ID: <code className="font-mono">{scanResult.run_id.slice(0, 8)}</code>
                {selectedSiteObj && (
                  <span className="ml-1">· ref: <strong>{selectedSiteObj.name}</strong></span>
                )}
              </span>
            )}
          </div>

          {scanResult?.ai_setup_note && (
            <div className="mt-3 flex items-start gap-2 text-xs text-slate-500 bg-slate-50 border rounded p-2.5">
              <Info className="h-4 w-4 mt-0.5 shrink-0 text-slate-400" />
              <span>{scanResult.ai_setup_note}</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Summary stats — only shown after a scan */}
      {scanResult && (
        <>
          <div className="flex gap-3 flex-wrap" data-testid="summary-stats">
            <StatCard label="Products Scanned" value={scanResult.total_products_scanned} />
            <StatCard label="Total Findings" value={scanResult.findings?.length}
              sub={`${scanResult.auto_apply_eligible} auto-apply eligible`} />
            <StatCard label="High" value={sevCounts.HIGH}
              colorClass={sevCounts.HIGH > 0 ? 'text-red-600' : 'text-slate-700'} />
            <StatCard label="Medium" value={sevCounts.MEDIUM}
              colorClass={sevCounts.MEDIUM > 0 ? 'text-amber-600' : 'text-slate-700'} />
            <StatCard label="Low" value={sevCounts.LOW} />
            <StatCard label="Task 1 Eligible" value={scanResult.task1_eligible}
              sub="≥99% confidence, Mercure ref"
              colorClass={scanResult.task1_eligible > 0 ? 'text-emerald-600' : 'text-slate-700'} />
            <StatCard label="Selected" value={selected.size}
              sub={selectedApplyCount > 0 ? `${selectedApplyCount} can auto-apply` : undefined}
              colorClass={selected.size > 0 ? 'text-sky-700' : 'text-slate-700'} />
          </div>

          {/* Findings table */}
          <Card data-testid="findings-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-500" />
                Findings
              </CardTitle>
            </CardHeader>
            <CardContent>
              <FindingsTable
                findings={scanResult.findings}
                selected={selected}
                onToggle={handleToggle}
                onToggleAll={handleToggleAll}
              />
            </CardContent>
          </Card>

          {/* Apply actions */}
          <Card data-testid="apply-actions-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <Shield className="h-4 w-4 text-sky-600" />
                Apply Changes
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                  <Switch
                    checked={dryRun}
                    onCheckedChange={setDryRun}
                    data-testid="dry-run-toggle"
                  />
                  <span className="font-medium">Dry-run mode</span>
                  <span className="text-slate-400 text-xs">
                    {dryRun ? '(no changes written)' : '(WILL write to database)'}
                  </span>
                </label>
              </div>

              {!dryRun && (
                <div className="mb-4 flex items-start gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-3">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                  <div>
                    <strong>Live write mode.</strong> Changes will be committed to the product master.
                    Only <code className="text-xs bg-amber-100 px-1 rounded">product_units_of_measure</code> flags
                    and <code className="text-xs bg-amber-100 px-1 rounded">product_name</code> are modified.
                    Site-specific tables are never touched. All changes are logged with a run ID.
                  </div>
                </div>
              )}

              <div className="flex flex-wrap gap-3">
                <Button
                  onClick={handleApplySelected}
                  disabled={applying || selected.size === 0 || selectedApplyCount === 0}
                  variant={dryRun ? 'outline' : 'default'}
                  data-testid="apply-selected-btn"
                >
                  {applying ? (
                    <RefreshCw className="h-4 w-4 mr-1.5 animate-spin" />
                  ) : (
                    <CheckCheck className="h-4 w-4 mr-1.5" />
                  )}
                  {dryRun ? 'Dry-run' : 'Apply'} Selected ({selectedApplyCount})
                </Button>

                <Button
                  onClick={handleApplyTask1}
                  disabled={applying || !referenceSiteId || scanResult.task1_eligible === 0}
                  variant={dryRun ? 'outline' : 'default'}
                  className={!dryRun && scanResult.task1_eligible > 0 ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
                  data-testid="apply-task1-btn"
                  title={!referenceSiteId ? 'Requires a reference site' : undefined}
                >
                  <ShieldCheck className="h-4 w-4 mr-1.5" />
                  {dryRun ? 'Dry-run' : 'Apply'} Task 1 High-Confidence ({scanResult.task1_eligible})
                </Button>
              </div>

              {/* Apply result */}
              {applyResult && (
                <div
                  className={`mt-4 rounded border p-4 text-sm ${
                    applyResult.site_isolation_proof?.unchanged === false
                      ? 'bg-red-50 border-red-300'
                      : 'bg-slate-50 border-slate-200'
                  }`}
                  data-testid="apply-result"
                >
                  <div className="flex items-center gap-2 font-semibold mb-2">
                    {applyResult.dry_run
                      ? <><Info className="h-4 w-4 text-sky-600" /> Dry-run preview</>
                      : <><CheckCheck className="h-4 w-4 text-emerald-600" /> Apply complete</>
                    }
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs mb-3">
                    <div><span className="text-slate-500">Applied / would apply:</span>{' '}
                      <strong className="text-emerald-700">{applyResult.applied}</strong></div>
                    <div><span className="text-slate-500">Skipped:</span>{' '}
                      <strong className="text-amber-700">{applyResult.skipped}</strong></div>
                    <div><span className="text-slate-500">Errors:</span>{' '}
                      <strong className="text-red-700">{applyResult.errors?.length ?? 0}</strong></div>
                    <div><span className="text-slate-500">Run ID:</span>{' '}
                      <code className="font-mono">{applyResult.run_id?.slice(0, 8)}</code></div>
                  </div>

                  {/* Site isolation proof */}
                  <div className={`rounded p-2.5 text-xs mb-2 ${
                    applyResult.site_isolation_proof?.unchanged
                      ? 'bg-emerald-50 border border-emerald-200 text-emerald-700'
                      : applyResult.site_isolation_proof?.unchanged === false
                        ? 'bg-red-50 border border-red-300 text-red-700'
                        : 'bg-slate-100 text-slate-500'
                  }`} data-testid="isolation-proof">
                    <strong>Site isolation:</strong>{' '}
                    {applyResult.site_isolation_proof?.note ?? 'N/A'}
                    {applyResult.site_isolation_proof?.before && (
                      <div className="mt-1 font-mono grid grid-cols-3 gap-x-4">
                        {Object.entries(applyResult.site_isolation_proof.before).map(([t, cnt]) => (
                          <div key={t}>
                            <span className="text-slate-400">{t.replace('site_', '')}:</span>{' '}
                            {cnt} → {applyResult.site_isolation_proof.after?.[t] ?? cnt}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {applyResult.errors?.length > 0 && (
                    <div className="text-xs text-red-600 mt-1">
                      {applyResult.errors.slice(0, 5).map((e, i) => <div key={i}>• {e}</div>)}
                    </div>
                  )}

                  {applyResult.changes?.length > 0 && (
                    <details className="mt-2">
                      <summary className="text-xs text-slate-500 cursor-pointer hover:text-slate-700">
                        Change detail ({applyResult.changes.length} items)
                      </summary>
                      <div className="mt-1 space-y-0.5 max-h-48 overflow-y-auto">
                        {applyResult.changes.map((c, i) => (
                          <div key={i} className="text-xs text-slate-600 font-mono border-b pb-0.5">
                            <span className={`mr-1 ${c.status === 'applied' || c.status === 'would_apply' ? 'text-emerald-600' : 'text-slate-400'}`}>
                              [{c.status}]
                            </span>
                            {c.product_name}: {c.change ?? c.proposed_change ?? '—'}
                          </div>
                        ))}
                      </div>
                    </details>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Task 1 — Selling-Measure Heal (dedicated explicit workflow) */}
      {scanResult && (
        <Card data-testid="task1-heal-card" className="border-emerald-200 bg-emerald-50/40">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold flex items-center gap-2 text-emerald-800">
              <Wrench className="h-4 w-4 text-emerald-600" />
              Task 1 — Selling-Measure Heal
              {scanResult.task1_eligible > 0 ? (
                <span className="ml-2 text-xs font-normal text-emerald-700 bg-emerald-100 border border-emerald-200 rounded px-2 py-0.5">
                  {scanResult.task1_eligible} candidate{scanResult.task1_eligible !== 1 ? 's' : ''} identified
                </span>
              ) : (
                <span className="ml-2 text-xs font-normal text-slate-500">No eligible candidates</span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Rule description */}
            <div className="rounded border border-emerald-200 bg-white p-3 text-sm space-y-1">
              <p className="font-medium text-slate-700">Rule: Selling-measure default fix</p>
              <ul className="text-xs text-slate-600 space-y-0.5 list-disc list-inside">
                <li>Scope: Products with no valid default selling measure set (null or mismatched)</li>
                <li>Evidence: ≥99% confidence from Mercure direct site reference</li>
                <li>Eligible categories: packaged Beer, Cider, Soft Drinks, Mixers, Wine</li>
                <li>Action: Updates <code className="bg-slate-100 px-1 rounded">product_units_of_measure</code> default flag only</li>
                <li>Never modifies: stock quantities, site overrides, client overrides, audit history</li>
                <li>Excludes: ambiguous cases, multi-measure products without clear evidence</li>
                <li>Never runs automatically on deployment</li>
              </ul>
            </div>

            {/* Candidates table */}
            {scanResult.task1_eligible > 0 && (() => {
              const task1Items = (scanResult.findings ?? []).filter(f => f.task1_eligible || (f.rule_id?.startsWith('R0') && f.confidence >= 99));
              return task1Items.length > 0 ? (
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
                    Candidates ({task1Items.length})
                  </p>
                  <div className="max-h-48 overflow-y-auto rounded border border-slate-200 bg-white">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50 border-b border-slate-200">
                        <tr>
                          <th className="px-3 py-1.5 text-left font-semibold text-slate-500">Product</th>
                          <th className="px-3 py-1.5 text-left font-semibold text-slate-500">Rule</th>
                          <th className="px-3 py-1.5 text-left font-semibold text-slate-500">Evidence / Issue</th>
                          <th className="px-3 py-1.5 text-left font-semibold text-slate-500">Current</th>
                          <th className="px-3 py-1.5 text-left font-semibold text-slate-500">Proposed</th>
                          <th className="px-3 py-1.5 text-right font-semibold text-slate-500">Conf</th>
                        </tr>
                      </thead>
                      <tbody>
                        {task1Items.map((f, i) => (
                          <tr key={f.finding_id} className="border-b border-slate-100 last:border-0" data-testid={`task1-row-${i}`}>
                            <td className="px-3 py-1.5 font-medium text-slate-700 max-w-[160px] truncate" title={f.product_name}>
                              {f.product_code && <span className="font-mono text-slate-400 mr-1">{f.product_code}</span>}
                              {f.product_name}
                            </td>
                            <td className="px-3 py-1.5 font-mono text-slate-500">{f.rule_id}</td>
                            <td className="px-3 py-1.5 text-slate-500 max-w-[200px] truncate" title={f.issue}>{f.issue}</td>
                            <td className="px-3 py-1.5 text-slate-400">{f.current_value ?? '—'}</td>
                            <td className="px-3 py-1.5 text-emerald-700 font-medium">{f.proposed_value ?? '—'}</td>
                            <td className="px-3 py-1.5 text-right text-slate-500">{f.confidence ?? '—'}%</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : null;
            })()}

            {/* Dry-run gate */}
            {scanResult.task1_eligible > 0 && (
              <div className="space-y-3">
                <div className="flex items-start gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-2.5">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>
                    <strong>Required:</strong> Run the dry-run first to preview exact changes.
                    The Apply button is only enabled after a dry-run has been completed in this session.
                  </span>
                </div>

                <div className="flex flex-wrap gap-3 items-center">
                  <Button
                    variant="outline"
                    onClick={handleTask1DryRun}
                    disabled={task1RunningDryRun || task1Applying}
                    data-testid="task1-dry-run-btn"
                    className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                  >
                    {task1RunningDryRun ? (
                      <RefreshCw className="h-4 w-4 mr-1.5 animate-spin" />
                    ) : (
                      <Play className="h-4 w-4 mr-1.5" />
                    )}
                    {task1RunningDryRun ? 'Running dry-run…' : 'Run Dry-run'}
                  </Button>

                  {task1DryRunDone && (
                    <Button
                      onClick={() => setTask1ApplyConfirm(true)}
                      disabled={task1Applying || !task1DryRunDone}
                      data-testid="task1-apply-btn"
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    >
                      <Wrench className="h-4 w-4 mr-1.5" />
                      {task1Applying ? 'Applying…' : `Apply Task 1 (${scanResult.task1_eligible} changes)`}
                    </Button>
                  )}
                </div>

                {/* Dry-run results */}
                {task1DryRunResult && (
                  <div className="rounded border border-sky-200 bg-sky-50 p-3 text-sm" data-testid="task1-dry-run-result">
                    <div className="flex items-center gap-2 font-semibold text-sky-700 mb-2">
                      <Info className="h-4 w-4" /> Dry-run preview — no changes written
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs mb-2">
                      <div><span className="text-slate-500">Would apply:</span>{' '}
                        <strong className="text-emerald-700">{task1DryRunResult.applied}</strong></div>
                      <div><span className="text-slate-500">Would skip:</span>{' '}
                        <strong className="text-amber-600">{task1DryRunResult.skipped}</strong></div>
                      <div><span className="text-slate-500">Errors:</span>{' '}
                        <strong className="text-red-600">{task1DryRunResult.errors?.length ?? 0}</strong></div>
                      <div><span className="text-slate-500">Run ID:</span>{' '}
                        <code className="font-mono text-xs">{task1DryRunResult.run_id?.slice(0, 8)}</code></div>
                    </div>
                    {task1DryRunResult.changes?.length > 0 && (
                      <details>
                        <summary className="text-xs text-slate-500 cursor-pointer hover:text-slate-700">
                          Before/after detail ({task1DryRunResult.changes.length} items)
                        </summary>
                        <div className="mt-1 space-y-0.5 max-h-36 overflow-y-auto">
                          {task1DryRunResult.changes.map((c, i) => (
                            <div key={i} className="text-xs font-mono text-slate-600 border-b pb-0.5">
                              <span className="text-sky-600 mr-1">[would_apply]</span>
                              {c.product_name}: {c.change ?? c.proposed_change ?? '—'}
                            </div>
                          ))}
                        </div>
                      </details>
                    )}
                    <p className="text-xs text-emerald-700 font-medium mt-2">
                      Dry-run complete — review above, then click "Apply Task 1" to commit.
                    </p>
                    {/* Export dry-run record */}
                    {task1DryRunResult.run_id && (
                      <div className="flex gap-2 mt-2">
                        <Button size="sm" variant="outline" className="h-6 text-xs px-2"
                          onClick={() => handleExport(task1DryRunResult.run_id, 'csv')}
                          data-testid="task1-export-csv">
                          <Download className="h-3 w-3 mr-1" />Export CSV
                        </Button>
                        <Button size="sm" variant="outline" className="h-6 text-xs px-2"
                          onClick={() => handleExport(task1DryRunResult.run_id, 'json')}
                          data-testid="task1-export-json">
                          <Download className="h-3 w-3 mr-1" />Export JSON
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {/* Apply result */}
                {task1ApplyResult && (
                  <div className="rounded border border-emerald-300 bg-emerald-50 p-3 text-sm" data-testid="task1-apply-result">
                    <div className="flex items-center gap-2 font-semibold text-emerald-700 mb-2">
                      <CheckCheck className="h-4 w-4" /> Task 1 applied — product master updated
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs mb-2">
                      <div><span className="text-slate-500">Applied:</span>{' '}
                        <strong className="text-emerald-700">{task1ApplyResult.applied}</strong></div>
                      <div><span className="text-slate-500">Skipped:</span>{' '}
                        <strong className="text-amber-600">{task1ApplyResult.skipped}</strong></div>
                      <div><span className="text-slate-500">Errors:</span>{' '}
                        <strong className="text-red-600">{task1ApplyResult.errors?.length ?? 0}</strong></div>
                      <div><span className="text-slate-500">Run ID:</span>{' '}
                        <code className="font-mono text-xs">{task1ApplyResult.run_id?.slice(0, 8)}</code></div>
                    </div>
                    {/* Export run record */}
                    {task1ApplyResult.run_id && (
                      <div className="flex gap-2 mt-1 mb-2">
                        <Button size="sm" variant="outline" className="h-6 text-xs px-2"
                          onClick={() => handleExport(task1ApplyResult.run_id, 'csv')}
                          data-testid="task1-applied-export-csv">
                          <Download className="h-3 w-3 mr-1" />Export run record CSV
                        </Button>
                        <Button size="sm" variant="outline" className="h-6 text-xs px-2"
                          onClick={() => handleExport(task1ApplyResult.run_id, 'json')}
                          data-testid="task1-applied-export-json">
                          <Download className="h-3 w-3 mr-1" />Export run record JSON
                        </Button>
                      </div>
                    )}
                    <div className="flex items-center gap-2 mt-2">
                      <p className="text-xs text-slate-600">
                        Run re-scan below to verify zero Task 1 candidates remain.
                      </p>
                      <Button variant="outline" size="sm" className="h-7 text-xs border-emerald-300 text-emerald-700"
                        onClick={handleTask1Rescan}
                        disabled={task1Rescanning}
                        data-testid="task1-rescan-btn"
                      >
                        {task1Rescanning ? <RefreshCw className="h-3 w-3 mr-1 animate-spin" /> : <RefreshCw className="h-3 w-3 mr-1" />}
                        {task1Rescanning ? 'Re-scanning…' : 'Re-scan to Verify'}
                      </Button>
                    </div>
                    {/* Re-scan result */}
                    {task1RescanResult && (
                      <div className={`mt-2 rounded border p-2 text-xs ${
                        task1RescanResult.task1_eligible === 0
                          ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
                          : 'border-amber-300 bg-amber-50 text-amber-700'
                      }`} data-testid="task1-rescan-result">
                        {task1RescanResult.task1_eligible === 0 ? (
                          <span className="font-semibold flex items-center gap-1">
                            <CheckCheck className="h-3.5 w-3.5" />
                            Re-scan complete — zero Task 1 candidates remaining. Heal is complete.
                          </span>
                        ) : (
                          <span className="font-semibold flex items-center gap-1">
                            <AlertCircle className="h-3.5 w-3.5" />
                            Re-scan found {task1RescanResult.task1_eligible} Task 1 candidate{task1RescanResult.task1_eligible !== 1 ? 's' : ''} still remaining.
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Zero candidates confirmation */}
            {scanResult.task1_eligible === 0 && (
              <div className="flex items-center gap-2 text-sm text-emerald-700">
                <CheckCheck className="h-4 w-4" />
                No Task 1 candidates in this scan — product master selling measures are up to date.
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Task 1 Apply Confirmation Dialog */}
      {task1ApplyConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-xl border border-slate-200 p-6 max-w-md w-full mx-4">
            <h3 className="text-base font-semibold text-slate-900 mb-2 flex items-center gap-2">
              <Wrench className="h-4 w-4 text-emerald-600" />
              Confirm Task 1 Apply
            </h3>
            <p className="text-sm text-slate-600 mb-3">
              This will commit {scanResult?.task1_eligible} selling-measure update{scanResult?.task1_eligible !== 1 ? 's' : ''} to
              the <strong>product master</strong> only. Site-level overrides and client overrides are not affected.
            </p>
            {envInfo?.is_production && (
              <div className="mb-3 rounded border border-red-300 bg-red-50 p-2.5 text-xs text-red-700 font-semibold">
                You are applying to PRODUCTION. Ensure the dry-run output above has been reviewed.
              </div>
            )}
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setTask1ApplyConfirm(false)} data-testid="task1-confirm-cancel">
                Cancel
              </Button>
              <Button
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                onClick={handleTask1Apply}
                disabled={task1Applying}
                data-testid="task1-confirm-apply"
              >
                {task1Applying ? 'Applying…' : 'Apply Task 1'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Run history */}
      <Card data-testid="run-history-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <ExternalLink className="h-4 w-4 text-slate-500" />
            Recent Audit Runs
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {runsLoading ? (
            <p className="text-sm text-slate-400 px-4 py-6 text-center">Loading…</p>
          ) : runs.length === 0 ? (
            <p className="text-sm text-slate-400 px-4 py-6 text-center">
              No audit runs yet. Click <strong>Run Scan</strong> above.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="run-history-table">
                <thead>
                  <tr className="border-b bg-slate-50 text-xs text-slate-500 font-medium uppercase tracking-wide">
                    <th className="px-3 py-2 text-left">Run ID</th>
                    <th className="px-3 py-2 text-left">Scanned at</th>
                    <th className="px-3 py-2 text-left">Scope</th>
                    <th className="px-3 py-2 text-left">Status</th>
                    <th className="px-3 py-2 text-right">Findings</th>
                    <th className="px-3 py-2 text-right">Auto-apply</th>
                    <th className="px-3 py-2 text-right">Applied</th>
                    <th className="px-3 py-2 text-left">Export</th>
                  </tr>
                </thead>
                <tbody>
                  {runs.map(run => (
                    <RunHistoryRow key={run.run_id} run={run} onExport={handleExport} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
